'use client'

import { forwardRef } from 'react'
import SelectComponent from 'react-select'

import { AttentionRoundIcon } from '@/components/icons'
import { useShareForwardedRef } from '@/utils/useShareForwardedRef'

import { ISelectProps } from './types'

import { Loading } from '..'

const Select = forwardRef<HTMLSelectElement, ISelectProps>(
  ({ wFull = false, ...props }, ref) => {
    const innerRef = useShareForwardedRef<any>(ref)
    const isError = props.errorMessage

    return (
      <div
        className={`flex flex-col gap-2 cursor-pointer
          ${wFull ? 'w-full' : ''}
          ${props.className}
        `}
      >
        {props.label ? (
          <label htmlFor={props.name} className="prose-body text-black w-full">
            {props.label}
          </label>
        ) : null}
        <div className="relative w-full">
          <SelectComponent
            placeholder={props.placeholder}
            name={props.name}
            id={props.name}
            instanceId={props.name}
            noOptionsMessage={() => 'Nenhuma opção encontrada'}
            classNames={{
              control: (state) =>
                `bg-white flex flex-row justify-center prose-body
                ${isError ? 'border-status-red' : 'border-grayscale-200'}
              ${
                state.hasValue || state.isFocused
                  ? 'text-black'
                  : 'text-grayscale-200'
              }
              ${state.isFocused && !isError ? 'border-2 border-primary-500' : 'border'}
              ${state.isFocused && isError ? 'border-2 border-status-red' : 'border'}
              ${state.menuIsOpen ? ' rounded-t-lg' : 'rounded-lg'}`,
              dropdownIndicator: (state) =>
                `${state.selectProps.menuIsOpen ? 'transform rotate-180' : ''}
              ${state.isFocused ? 'text-primary-500' : 'text-grayscale-200'}
            `,
              menuList: () =>
                `pr-1 overflow-y-auto overflow-x-hidden scrollbar-thin scrollbar-thumb-primary-500
                scrollbar-track-grayscale-100 scrollbar-thumb-rounded scrollbar-track-rounded`,
              option: () =>
                `prose-body text-grayscale-800 hover:text-black hover:cursor-pointer hover:bg-primary-500
                hover:bg-opacity-10 prose-body py-3.5 px-4 aria-disabled:bg-grayscale-50 aria-disabled:text-grayscale-300
                aria-disabled:hover:bg-grayscale-50 aria-disabled:hover:text-grayscale-300 aria-disabled:hover:cursor-default
                aria-selected:bg-primary-500 aria-selected:bg-opacity-30 aria-selected:text-black
                `,
              menu: () =>
                'h-auto bg-white py-1 border-x border-b border-grayscale-200 rounded-b-lg',
              valueContainer: () => `${props.isLoading ? 'pr-8' : ''}`,
            }}
            styles={{
              clearIndicator: () => ({
                display: 'none',
              }),
              loadingIndicator: () => ({
                display: 'none',
              }),
              control: (_base, props) => ({
                minWidth: '200px',
                minHeight: '50px',
                maxHeight: '50px',
                padding: props.isFocused ? '13px 15px' : '14px 16px',
              }),
            }}
            unstyled
            {...props}
            ref={innerRef}
          />
          {props.isLoading ? (
            <div className="absolute w-6 h-6 right-11 top-1/2 -translate-y-1/2">
              <Loading variant="secondary" />
            </div>
          ) : null}
        </div>
        {props.errorMessage ? (
          <span className="flex gap-1 items-center text-status-red prose-caption">
            <AttentionRoundIcon className="fill-status-red w-4 h-4" />
            {props.errorMessage}
          </span>
        ) : null}
      </div>
    )
  },
)

export default Select
